import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Users, BarChart3, MousePointer, Package, Clock, AlertCircle } from 'lucide-react';
import { reportApi } from '@/lib/api';
import { formatNumber, formatDate } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function ShareableReport() {
  const { reportToken } = useParams();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReport();
  }, [reportToken]);

  const loadReport = async () => {
    try {
      const response = await reportApi.getShareableReport(reportToken);
      setReport(response.data);
    } catch (error) {
      if (error.response?.status === 410) {
        setError('This report link has expired');
      } else if (error.response?.status === 404) {
        setError('Report not found');
      } else {
        setError('Failed to load report');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="report-error">
        <div className="bg-white rounded-2xl p-8 border border-slate-100 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Report Unavailable</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            Go to MEMORA Home
          </Link>
        </div>
      </div>
    );
  }

  const { campaign, analytics } = report;

  const chartData = [
    { name: 'Notes', value: analytics.notes_uploaded },
    { name: 'Documents', value: analytics.documents_uploaded },
    { name: 'CTA Clicks', value: analytics.cta_clicks }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shareable-report">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">M</span>
              </div>
              <div>
                <p className="text-sm text-slate-500">Campaign Report</p>
                <h1 className="font-bold text-xl text-slate-900">{campaign.name}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              <span>View Only</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Distributed</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(analytics.tokens_distributed)}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Activated</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{formatNumber(analytics.tokens_activated)}</p>
            <p className="text-sm text-emerald-600">{analytics.activation_rate}% rate</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">MAU</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{formatNumber(analytics.monthly_active_users)}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <MousePointer className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">CTA Clicks</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{formatNumber(analytics.cta_clicks)}</p>
            <p className="text-sm text-amber-600">{analytics.ctr}% CTR</p>
          </motion.div>
        </div>

        {/* Engagement Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-6 border border-slate-100 mb-8"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Engagement Overview</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Campaign Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Campaign Details</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500">Campaign Name</p>
              <p className="font-medium text-slate-900">{campaign.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Artifact Type</p>
              <p className="font-medium text-slate-900 capitalize">{campaign.artifact_type}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Start Date</p>
              <p className="font-medium text-slate-900">{formatDate(campaign.start_date)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Engagement</p>
              <p className="font-medium text-slate-900">
                {formatNumber(analytics.notes_uploaded + analytics.documents_uploaded)} interactions
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Report generated by MEMORA • {formatDate(new Date())}</p>
        </div>
      </main>
    </div>
  );
}
